import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, renderSlot as _renderSlot, withModifiers as _withModifiers, withKeys as _withKeys, Transition as _Transition, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "admin__side" }
const _hoisted_2 = { class: "admin__brand" }
const _hoisted_3 = { class: "admin__brand-mark" }
const _hoisted_4 = { class: "admin__me" }
const _hoisted_5 = { class: "admin__me-name" }
const _hoisted_6 = { class: "admin__me-meta" }
const _hoisted_7 = { class: "admin__role" }
const _hoisted_8 = {
  class: "admin__nav",
  "aria-label": "관리자 메뉴"
}
const _hoisted_9 = { class: "admin__nav-title" }
const _hoisted_10 = {
  key: 0,
  class: "admin__nav-badge"
}
const _hoisted_11 = { class: "admin__side-foot" }
const _hoisted_12 = {
  class: "admin__side-link",
  href: "/",
  target: "_blank",
  rel: "noopener"
}
const _hoisted_13 = { class: "admin__body" }
const _hoisted_14 = { class: "admin__top" }
const _hoisted_15 = ["aria-expanded"]
const _hoisted_16 = {
  class: "admin__top-title",
  "data-tour": "page-title"
}
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { class: "admin__top-right" }
const _hoisted_19 = { class: "admin__today" }
const _hoisted_20 = { class: "admin__main" }
const _hoisted_21 = { class: "admin-visit__head" }
const _hoisted_22 = {
  key: 0,
  class: "sui-toast"
}

import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import FeatureSpecPanel from '@/components/manage/FeatureSpecPanel.vue'
import FeatureTourOverlay from '@/components/manage/FeatureTourOverlay.vue'
import VisitorStatsWidget from '@/components/home/VisitorStatsWidget.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useManageUiScale } from '@/composables/useManageUiScale'
import { useAdminProducts } from '@/composables/useAdminProducts'
import {
  fetchAdminOrders,
  fetchAdminSettlementRequests,
} from '@/api/mallOrder'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminLayout',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { admin, logout, isSuperAdmin } = useAdminAuth()
const { scaleStyle } = useManageUiScale()
const { counts, toast, load } = useAdminProducts()

/* 어느 화면으로 들어와도 사이드바 검수 대기 배지는 맞아야 한다 */
onMounted(() => {
  if (!Object.keys(counts.value).length) {
    load()
  }
})

const navOpen = ref(false)

const pendingCount = computed(() => counts.value.REQUESTED ?? 0)
const waitingPayment = ref(0)
const scheduledPayouts = ref(0)

/* 입금·클레임·정산 대기 건수는 사이드바에서 바로 보이게 둔다 */
onMounted(async () => {
  try {
    const [orders, settlements] = await Promise.all([
      fetchAdminOrders({ status: 'PENDING' }).catch(() => null),
      fetchAdminSettlementRequests({ status: 'REQUESTED' }).catch(() => null),
    ])
    waitingPayment.value = (orders?.orders || []).filter((order) => order.payMethod !== 'TOSS').length
    scheduledPayouts.value = settlements?.requestedCount ?? 0
  } catch {
    waitingPayment.value = 0
  }
})

const navGroups = computed(() => [
  {
    title: '현황',
    items: [{ label: '대시보드', to: '/admin', icon: 'mdi-view-dashboard-outline', badge: 0 }],
  },
  {
    title: '주문',
    items: [
      {
        label: '주문 · 입금',
        to: '/admin/orders',
        icon: 'mdi-receipt-text-outline',
        badge: waitingPayment.value,
      },
      {
        label: '정산',
        to: '/admin/settlements',
        icon: 'mdi-cash-multiple',
        badge: scheduledPayouts.value,
      },
    ],
  },
  {
    title: '상품',
    items: [
      {
        label: '상품관리',
        to: '/admin/products',
        icon: 'mdi-package-variant-closed',
        badge: pendingCount.value,
      },
      {
        label: '이달의 특가',
        to: '/admin/deals',
        icon: 'mdi-sale',
        badge: 0,
      },
      {
        label: '공동구매 특별할인',
        to: '/admin/sales',
        icon: 'mdi-calendar-range',
        badge: 0,
      },
      {
        label: '문의 · 후기',
        to: '/admin/cs',
        icon: 'mdi-message-text-outline',
        badge: 0,
      },
    ],
  },
  {
    title: '회원',
    items: [
      { label: '판매업체', to: '/admin/sellers', icon: 'mdi-storefront-outline', badge: 0 },
      {
        label: '고객관리',
        to: '/admin/members',
        icon: 'mdi-office-building-outline',
        badge: 0,
      },
    ],
  },
  {
    title: '설정 · 콘텐츠',
    items: [
      { label: '입금 계좌', to: '/admin/settings', icon: 'mdi-bank-outline', badge: 0 },
      {
        label: '약관 CMS',
        to: '/admin/legal-docs',
        icon: 'mdi-file-document-outline',
        badge: 0,
      },
      {
        label: '화면 멘트',
        to: '/admin/content-snippets',
        icon: 'mdi-text-box-outline',
        badge: 0,
      },
      ...(isSuperAdmin.value
        ? [
            {
              label: '판매업체 화면 보기',
              to: '/admin/seller-view',
              icon: 'mdi-account-switch-outline',
              badge: 0,
            },
          ]
        : []),
    ],
  },
])

function isActive(to: string): boolean {
  if (to === '/admin') {
    return route.path === '/admin'
  }
  return route.path.startsWith(to)
}

const pageTitle = computed(() => (route.meta.adminTitle as string) ?? '관리자')
const pageDesc = computed(() => (route.meta.adminDesc as string) ?? '')

const today = new Intl.DateTimeFormat('ko-KR', {
  month: 'long',
  day: 'numeric',
  weekday: 'short',
}).format(new Date())

const visitStatsOpen = ref(false)
const visitPanelRef = ref<HTMLElement | null>(null)

watch(visitStatsOpen, (open) => {
  if (open) void nextTick(() => visitPanelRef.value?.focus())
})

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false
    visitStatsOpen.value = false
  },
)

async function onLogout() {
  if (!window.confirm('관리자 화면에서 로그아웃할까요?')) {
    return
  }
  await logout()
  router.push({ path: '/login', query: { tab: 'admin' } })
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["admin", { 'admin--nav-open': navOpen.value }]),
    style: _normalizeStyle(_unref(scaleStyle))
  }, [
    _createElementVNode("aside", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", _hoisted_3, [
          _createVNode(AppIcon, {
            icon: "mdi-shield-crown-outline",
            size: 22
          })
        ]),
        _cache[7] || (_cache[7] = _createElementVNode("span", { class: "admin__brand-text" }, [
          _createElementVNode("strong", null, "함우리몰"),
          _createElementVNode("em", null, "관리자")
        ], -1))
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(admin)?.name || '관리자'), 1),
        _createElementVNode("p", _hoisted_6, [
          _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(admin)?.roleName || ''), 1),
          _createElementVNode("span", null, _toDisplayString(_unref(admin)?.userId), 1)
        ])
      ]),
      _createElementVNode("nav", _hoisted_8, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navGroups.value, (group) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: group.title
          }, [
            _createElementVNode("p", _hoisted_9, _toDisplayString(group.title), 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item) => {
              return (_openBlock(), _createBlock(_component_router_link, {
                key: item.to,
                to: item.to,
                class: _normalizeClass(["admin__nav-link", { 'is-active': isActive(item.to) }]),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (navOpen.value = false))
              }, {
                default: _withCtx(() => [
                  _createVNode(AppIcon, {
                    icon: item.icon,
                    size: 19
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", null, _toDisplayString(item.label), 1),
                  (item.badge)
                    ? (_openBlock(), _createElementBlock("em", _hoisted_10, _toDisplayString(item.badge), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["to", "class"]))
            }), 128))
          ], 64))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("a", _hoisted_12, [
          _createVNode(AppIcon, {
            icon: "mdi-open-in-new",
            size: 17
          }),
          _cache[8] || (_cache[8] = _createTextVNode(" 사이트 보기 ", -1))
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "admin__side-link",
          onClick: onLogout
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-logout-variant",
            size: 17
          }),
          _cache[9] || (_cache[9] = _createTextVNode(" 로그아웃 ", -1))
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createElementVNode("header", _hoisted_14, [
        _createElementVNode("button", {
          type: "button",
          class: "admin__nav-toggle",
          "aria-expanded": navOpen.value,
          "aria-label": "메뉴 열기",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (navOpen.value = !navOpen.value))
        }, [
          _createVNode(AppIcon, {
            icon: navOpen.value ? 'mdi-close' : 'mdi-menu',
            size: 22
          }, null, 8, ["icon"])
        ], 8, _hoisted_15),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("h1", null, _toDisplayString(pageTitle.value), 1),
          (pageDesc.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(pageDesc.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("button", {
            type: "button",
            class: "admin__visit-btn",
            "data-tour": "admin-visit-stats",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (visitStatsOpen.value = true))
          }, [
            _createVNode(AppIcon, {
              icon: "mdi-chart-box-outline",
              size: 18
            }),
            _cache[10] || (_cache[10] = _createTextVNode(" 방문자 통계 ", -1))
          ]),
          _createVNode(FeatureSpecPanel),
          _createElementVNode("span", _hoisted_19, _toDisplayString(_unref(today)), 1),
          (waitingPayment.value)
            ? (_openBlock(), _createBlock(_component_router_link, {
                key: 0,
                to: "/admin/orders",
                class: "admin__pending is-order"
              }, {
                default: _withCtx(() => [
                  _createVNode(AppIcon, {
                    icon: "mdi-cash-clock",
                    size: 17
                  }),
                  _createTextVNode(" 입금 확인 " + _toDisplayString(waitingPayment.value) + "건 ", 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (pendingCount.value)
            ? (_openBlock(), _createBlock(_component_router_link, {
                key: 1,
                to: "/admin/products?status=REQUESTED",
                class: "admin__pending"
              }, {
                default: _withCtx(() => [
                  _createVNode(AppIcon, {
                    icon: "mdi-clipboard-alert-outline",
                    size: 17
                  }),
                  _createTextVNode(" 검수요청 " + _toDisplayString(pendingCount.value) + "건 ", 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _createVNode(FeatureTourOverlay),
      _createElementVNode("main", _hoisted_20, [
        _renderSlot(_ctx.$slots, "default")
      ])
    ]),
    (navOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "admin__scrim",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (navOpen.value = false))
        }))
      : _createCommentVNode("", true),
    (visitStatsOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "admin-visit",
          onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (visitStatsOpen.value = false), ["self"])),
          onKeydown: _cache[6] || (_cache[6] = _withKeys(_withModifiers(($event: any) => (visitStatsOpen.value = false), ["prevent"]), ["esc"]))
        }, [
          _createElementVNode("div", {
            class: "admin-visit__panel",
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "admin-visit-title",
            tabindex: "-1",
            ref_key: "visitPanelRef",
            ref: visitPanelRef
          }, [
            _createElementVNode("header", _hoisted_21, [
              _cache[11] || (_cache[11] = _createElementVNode("h2", { id: "admin-visit-title" }, "방문자 통계", -1)),
              _createElementVNode("button", {
                type: "button",
                class: "admin-visit__close",
                "aria-label": "닫기",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (visitStatsOpen.value = false))
              }, [
                _createVNode(AppIcon, {
                  icon: "mdi-close",
                  size: 20
                })
              ])
            ]),
            _createVNode(VisitorStatsWidget, { plain: "" })
          ], 512)
        ], 32))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "sui-toast" }, {
      default: _withCtx(() => [
        (_unref(toast))
          ? (_openBlock(), _createElementBlock("p", _hoisted_22, [
              _createVNode(AppIcon, {
                icon: "mdi-check-circle",
                size: 18
              }),
              _createTextVNode(" " + _toDisplayString(_unref(toast)), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 6))
}
}

})